/**
 * Logger Utility
 * 
 * A configurable logger that can be disabled in production.
 * Supports different log levels and can be configured via environment variables.
 */

import { getCurrentToken } from '@/features/auth/utils/tokenManager';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
    /** Enable or disable all logging */
    enabled: boolean;
    /** Minimum log level to display */
    minLevel: LogLevel;
    /** Prefix to add to all log messages */
    prefix?: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
};

// Default config based on environment
const defaultConfig: LoggerConfig = {
    enabled: import.meta.env.DEV,
    minLevel: 'debug',
    prefix: '[IGA]',
};

let currentConfig: LoggerConfig = { ...defaultConfig };

/**
 * Configure the logger at runtime.
 */
export function configureLogger(config: Partial<LoggerConfig>): void {
    currentConfig = { ...currentConfig, ...config };
}

/**
 * Check if a log level should be displayed.
 */
function shouldLog(level: LogLevel): boolean {
    if (!currentConfig.enabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[currentConfig.minLevel];
}

/**
 * Format the log message with prefix and timestamp.
 */
function formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString().slice(11, 19);
    const prefix = currentConfig.prefix ? `${currentConfig.prefix} ` : '';
    return `${prefix}[${timestamp}] [${level.toUpperCase()}] ${message}`;
}

// ─── Envio de erros para o backend (aba Logs do admin) ─────────────────────────
// Independente do gate de console (currentConfig.enabled): em produção o console
// fica mudo, mas os erros DEVEM continuar sendo persistidos no backend.
// Usa fetch cru (NÃO httpClient, que importa este logger → loop/ciclo) e tem
// guarda de reentrância + teto por sessão para nunca virar fonte de spam.
const CLIENT_LOG_URL =
    (import.meta.env.VITE_BASE_URL || 'http://localhost:8888') + '/api/v1/client-logs';
const MAX_REPORTS_PER_SESSION = 50;
const reportedKeys = new Set<string>();
let reportingDepth = 0;
let reportedCount = 0;

function shipErrorToBackend(message: string, args: unknown[]): void {
    if (reportingDepth > 0) return;                  // evita recursão (ex.: erro dentro do report)
    if (reportedCount >= MAX_REPORTS_PER_SESSION) return;

    const err = args.find((a): a is Error => a instanceof Error);
    const errorType = err?.name;
    // Compõe a mensagem com o texto do Error (que costuma vir como arg separado),
    // ex.: "Erro ao carregar planilhas: Unsupported ZIP file".
    const fullMessage = err?.message ? `${message} ${err.message}` : message;
    const key = `${errorType ?? ''}|${fullMessage}`;
    if (reportedKeys.has(key)) return;               // dedup por sessão

    const token = getCurrentToken();
    if (!token) return;                              // só reporta usuário autenticado

    reportedKeys.add(key);
    reportedCount++;
    reportingDepth++;
    try {
        const extras = args.filter((a) => !(a instanceof Error));
        void fetch(CLIENT_LOG_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            keepalive: true,
            body: JSON.stringify({
                message: fullMessage.slice(0, 4000),
                level: 'error',
                error_type: errorType,
                stack: err?.stack?.slice(0, 16000),
                url: typeof window !== 'undefined' ? window.location.href : undefined,
                user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
                context: extras.length ? { args: extras.map((a) => String(a)).slice(0, 10) } : undefined,
            }),
        }).catch(() => { /* nunca propaga nem loga (evita recursão) */ });
    } catch {
        /* swallow */
    } finally {
        reportingDepth--;
    }
}

/**
 * Logger object with methods for each log level.
 */
export const logger = {
    debug(message: string, ...args: unknown[]): void {
        if (shouldLog('debug')) {
            console.log(formatMessage('debug', message), ...args);
        }
    },

    info(message: string, ...args: unknown[]): void {
        if (shouldLog('info')) {
            console.info(formatMessage('info', message), ...args);
        }
    },

    warn(message: string, ...args: unknown[]): void {
        if (shouldLog('warn')) {
            console.warn(formatMessage('warn', message), ...args);
        }
    },

    error(message: string, ...args: unknown[]): void {
        if (shouldLog('error')) {
            console.error(formatMessage('error', message), ...args);
        }
        // Persiste no backend SEMPRE (mesmo em produção, onde o console é mudo).
        shipErrorToBackend(message, args);
    },

    /** Log a group of related messages (only in dev mode) */
    group(label: string, fn: () => void): void {
        if (shouldLog('debug')) {
            console.group(formatMessage('debug', label));
            fn();
            console.groupEnd();
        }
    },

    /** Log a table (only in dev mode) */
    table(data: unknown): void {
        if (shouldLog('debug')) {
            console.table(data);
        }
    },
};

export default logger;
